import { authStore } from './stores/auth.svelte.ts';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

function localDevBase(): string {
  return `http://${import.meta.env.VITE_API_HOST ?? 'localhost'}:${import.meta.env.VITE_API_PORT ?? 3000}`;
}

const PAGES_PREVIEW_API_FALLBACK = 'https://agentboard-api.cashcon57.workers.dev';

export function apiBase(): string {
  const explicit = import.meta.env.VITE_API_BASE as string | undefined;
  if (explicit) return trimTrailingSlash(explicit);
  if (import.meta.env.DEV) return localDevBase();
  if (typeof window !== 'undefined') {
    if (window.location.hostname.endsWith('.pages.dev')) {
      return PAGES_PREVIEW_API_FALLBACK;
    }
    return trimTrailingSlash(window.location.origin);
  }
  return localDevBase();
}

export function apiUrl(path: string): string {
  return `${apiBase()}${path}`;
}

export function isLocalApiBase(base = apiBase()): boolean {
  try {
    const hostname = new URL(base).hostname.replace(/^\[|\]$/g, '').toLowerCase();
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname === '::1' ||
      hostname.endsWith('.local') ||
      hostname.endsWith('.localhost') ||
      hostname === 'tauri.localhost'
    );
  } catch {
    return base.includes('localhost') || base.includes('127.0.0.1');
  }
}

function buildHeaders(initHeaders?: HeadersInit): Headers {
  const headers = new Headers(initHeaders);
  const token = authStore.session?.access_token;
  if (token && !isLocalApiBase()) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(apiUrl(path), {
    ...init,
    headers: buildHeaders(init?.headers),
  });
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    // Error response might be JSON or plain text — try both.
    // Clone the response so we can read the body twice if needed.
    let errBody: unknown = null;
    try {
      errBody = await res.json();
    } catch {
      try {
        errBody = { message: await res.text() };
      } catch {
        errBody = { message: res.statusText };
      }
    }
    const message =
      (errBody && typeof errBody === 'object' && 'message' in errBody
        ? (errBody as { message?: string }).message
        : null) ?? res.statusText;
    throw Object.assign(new Error(message), { status: res.status, body: errBody });
  }
  // 204 No Content (DELETE, some POSTs) has an empty body — res.json()
  // would throw 'Unexpected end of JSON input'. Return undefined cast as T
  // so callers that don't need a return value get a clean resolve.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  // Some endpoints return empty 200 — be defensive
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
